<template>
  <!--
    合作伙伴双排无缝滚动（2026-09-09 方案一：同向慢滚）
    - 纯 CSS animation（translate3d -50% 循环），0 依赖，老内核兼容
    - 卡片只显示 logo：有素材出图，无素材（含预留位）出文字标识 wordmark
    - 克隆组 mounted 后才插入（v-if）→ 预渲染 HTML 只有一份，SEO 无重复内容
    - 克隆节点 aria-hidden + alt 置空 + 无交互（pointer-events:none）
    - track 不用 gap，卡片 margin-right 定距 → 位移 -50% 恰为一组宽，接缝无缝
    - hover/focus 暂停；离开视口 IntersectionObserver 暂停；reduced-motion 与
      ≤767px 退化为静态两列网格（克隆组在移动端隐藏）
    - 参数来源：ref-partners/合作伙伴滚动方案调研报告.md（钉钉/致远互联/安恒）
  -->
  <div class="pmarquee" :class="{ 'is-paused': !inView }">
    <div class="pmarquee-row" v-for="(row, r) in rows" :key="r">
      <div
        class="pmarquee-track"
        ref="tracks"
        :style="{ animationDuration: durations[r] + 's' }"
      >
        <div class="pmarquee-group">
          <div
            class="pmarquee-card"
            v-for="(item, i) in row"
            :key="'o' + i"
            :class="{ 'is-pending': item.pending }"
          >
            <div class="pmarquee-logo">
              <img v-if="item.logo" :src="item.logo" :alt="item.name" />
              <span
                v-else
                class="pmarquee-wordmark"
                :class="{ 'is-pending': item.pending }"
                >{{ item.name }}</span
              >
            </div>
          </div>
        </div>
        <div class="pmarquee-group is-clone" aria-hidden="true" v-if="mounted">
          <div
            class="pmarquee-card"
            v-for="(item, i) in row"
            :key="'c' + i"
            :class="{ 'is-pending': item.pending }"
            tabindex="-1"
          >
            <div class="pmarquee-logo">
              <img v-if="item.logo" :src="item.logo" alt="" />
              <span v-else class="pmarquee-wordmark">{{ item.name }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
const GAP = 20; // 卡片间距 px（与 .pmarquee-card 的 margin-right 一致）
const SPEED = 40; // px/s，调研定稿：>70 开始廉价

export default {
  name: "PartnerMarquee",
  props: {
    list: { type: Array, required: true },
  },
  data() {
    return { mounted: false, inView: true, durations: [55, 55] };
  },
  computed: {
    // 过滤掉未确认名单的占位项（pending:true 不渲染，避免"预留位"字样被预渲染进产线 HTML）；
    // 真实名单确认后删掉 partners.js 里的 pending 字段即自动恢复展示
    validList() {
      return this.list.filter((it) => !it.pending);
    },
    // 上排多 1 家（奇数时）
    rows() {
      const half = Math.ceil(this.validList.length / 2);
      return [this.validList.slice(0, half), this.validList.slice(half)];
    },
  },
  methods: {
    // 实测一组宽度（卡片 offsetLeft 间距法，不受 transform 影响），
    // 修正老内核（Chrome 78）对 width:max-content 的嵌套 flex 算宽不准的问题：
    // 组盒与 track 全部显式定宽 → translateX(-50%) 恰好移动一组 → 无缝；时长按实测算
    // 窄屏（≤767px）是 CSS 静态网格，必须跳过像素级定宽校准：
    // 换行后最后一张卡会落回左列，last.offsetLeft 退化为 0，
    // 算出的"一组宽"只有实际半组 → 写进 group 后把卡片压成细长条。
    isNarrow() {
      return typeof window !== "undefined" && window.innerWidth <= 767;
    },
    clearInlineWidth() {
      (this.$refs.tracks || []).forEach((t) => {
        t.style.width = "";
        [...t.children].forEach((g) => {
          g.style.width = "";
        });
      });
    },
    measure() {
      if (this.isNarrow()) {
        this.clearInlineWidth();
        return;
      }
      (this.$refs.tracks || []).forEach((t, i) => {
        const group = t && t.querySelector(".pmarquee-group");
        const cards = group && group.children;
        if (!cards || cards.length < 2) return;
        const first = cards[0];
        const last = cards[cards.length - 1];
        const gw =
          last.offsetLeft + last.offsetWidth + GAP - first.offsetLeft;
        if (gw <= 0) return;
        if (Math.abs(t.getBoundingClientRect().width - gw * 2) > 2) {
          t.style.width = gw * 2 + "px";
          // 组盒也要显式定宽：Chrome 78 会把 flex 组盒算窄导致接缝卡片重叠
          [...t.children].forEach((g) => {
            g.style.width = gw + "px";
          });
        }
        this.$set(this.durations, i, Math.round(gw / SPEED));
      });
    },
  },
  mounted() {
    // 预渲染（PrerenderSPAPlugin 跑 Puppeteer）时 navigator.webdriver === true，
    // 此时不能渲染克隆组，否则产线 HTML 里名单出现两份（SEO 重复内容）。
    // 真实浏览器 webdriver 为 false，hydrate 后才插入克隆组。
    if (!window.navigator || !window.navigator.webdriver) {
      this.mounted = true;
    }
    // 离开视口暂停，回来续播（避免页脚还在滚）
    if (typeof IntersectionObserver !== "undefined") {
      this._io = new IntersectionObserver(
        (entries) => {
          this.inView = entries[0].isIntersecting;
        },
        { threshold: 0 }
      );
      this._io.observe(this.$el);
    }
    // 宽度校准：克隆组插入后 + 窗口 load（字体/图片就位）各测一次
    this.$nextTick(this.measure);
    this._onLoad = () => this.measure();
    if (document.readyState !== "complete") {
      window.addEventListener("load", this._onLoad);
    }
    // 断点切换：窄屏清掉像素级内联宽交回 CSS，回到宽屏重新校准
    this._onResize = () => {
      if (this.isNarrow()) {
        this.clearInlineWidth();
      } else {
        this.$nextTick(this.measure);
      }
    };
    window.addEventListener("resize", this._onResize);
  },
  beforeDestroy() {
    if (this._io) this._io.disconnect();
    if (this._onLoad) window.removeEventListener("load", this._onLoad);
    if (this._onResize) window.removeEventListener("resize", this._onResize);
  },
};
</script>

<style scoped>
.pmarquee {
  max-width: 1200px;
  margin: 0 auto;
}

.pmarquee-row {
  position: relative;
  overflow: hidden;
}

.pmarquee-row + .pmarquee-row {
  margin-top: 20px;
}

.pmarquee-track {
  display: flex;
  width: max-content;
  will-change: transform;
  animation: pm-scroll linear infinite;
  animation-delay: 2s; /* 进场静止 2s 再启动，第一眼是稳的 */
}

.pmarquee.is-paused .pmarquee-track,
.pmarquee-row:hover .pmarquee-track,
.pmarquee-row:focus-within .pmarquee-track {
  animation-play-state: paused;
}

@keyframes pm-scroll {
  from {
    transform: translate3d(0, 0, 0);
  }
  to {
    transform: translate3d(-50%, 0, 0);
  }
}

.pmarquee-group {
  display: flex;
  flex: 0 0 auto; /* 禁止组收缩：老内核 max-content 算宽不准时由 JS 校准 track 宽 */
}

.pmarquee-card {
  flex: 0 0 180px;
  margin-right: 20px;
  background: #ffffff;
  border: 1px solid #e4e4e4;
  border-radius: 2px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  transition: border-color 0.3s ease, box-shadow 0.3s ease;
}

.pmarquee-card:hover {
  border-color: #e6004c;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

/* logo 区：卡片只留 logo，不再有名称条 */
.pmarquee-logo {
  height: 110px;
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px 14px;
  background: #ffffff;
}

/* 统一 logo 尺寸：所有 logo 居中衬入同一外接框（宽≤卡片内容区、高≤60px），
   以 object-fit:contain 等比缩放后居中 —— 无论原图是方形 / 长横版 / 竖版均视觉一致，
   且绝不溢出卡片。相比旧方案（固定 height:56px + max-width:88% 兜底），本方案让超宽
   长条 logo（如三亚市人民医院 699×194、一龄 243×45、海口横版组合）整体收进框内居中，
   不再被压成小于 56px 的矮条，消除「宽 logo 偏小」的视觉落差 */
.pmarquee-logo img {
  width: auto;
  height: auto;
  max-width: 100%;
  max-height: 60px;
  object-fit: contain;
  transition: opacity 0.3s ease;
}

/* 无 logo 素材的单位：单位名作文字标识（不是名称条） */
.pmarquee-wordmark {
  display: inline-block;
  max-width: 100%;
  color: #444444;
  font-size: 15px;
  font-weight: 500;
  line-height: 1.3;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: color 0.3s ease;
}

.pmarquee-card:hover .pmarquee-wordmark {
  color: #e6004c;
}

/* 克隆组：仅供无缝循环，不参与交互与语义 */
.pmarquee-group.is-clone {
  pointer-events: none;
}

/* 预留位：虚线弱化卡，名单确认后由数据侧替换 */
.pmarquee-card.is-pending {
  border-style: dashed;
  border-color: #d9d9d9;
  background: rgba(255, 255, 255, 0.55);
}

.pmarquee-wordmark.is-pending {
  color: #bbbbbb;
  font-weight: 400;
  font-size: 13px;
}

.pmarquee-card.is-pending:hover {
  border-color: #d9d9d9;
  box-shadow: none;
}

.pmarquee-card.is-pending:hover .pmarquee-wordmark {
  color: #bbbbbb;
}

/* 2026-09-11 用户要求「不要渐变」：已移除原左右各 140px、向 #efefef 淡出的渐变遮罩
   （.pmarquee-row::before/::after）。现卡片在 .pmarquee-row 的 overflow:hidden 边界直接硬裁，
   边缘不再做淡出过渡。若要恢复，见备份 PartnerMarquee.vue.bak-0911-nofade。 */

/* 动效降级 */
@media (prefers-reduced-motion: reduce) {
  .pmarquee-track {
    animation: none;
  }
}

/* 移动端：静态两列网格，克隆组隐藏 */
@media (max-width: 767px) {
  .pmarquee-row {
    overflow: visible;
  }

  .pmarquee-track {
    animation: none;
    width: auto !important; /* 覆盖 JS 校准的内联宽度，回退静态网格 */
    flex-wrap: wrap;
  }

  .pmarquee-group {
    flex-wrap: wrap;
    /* !important 必须保留：JS 会给组盒写像素内联宽，若覆盖不掉，
       窄屏换行算出的窄宽会把卡片压成细长条 */
    width: 100% !important;
  }

  .pmarquee-group.is-clone {
    display: none;
  }

  .pmarquee-card {
    flex: 0 0 calc(50% - 10px);
    margin-right: 10px;
    margin-bottom: 12px;
  }

  .pmarquee-logo {
    height: 92px;
  }

  .pmarquee-logo img {
    max-width: 100%;
    max-height: 44px;
    width: auto;
    height: auto;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/PartnerMarquee.vue